import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/store";
import { toast } from "sonner";
import { Truck, CheckCircle2, AlertTriangle, ExternalLink, Save, Zap } from "lucide-react";

/**
 * Admin form to configure UPS shipping credentials.
 * Values are persisted in the `settings` collection under key="ups".
 * Backend also reads .env as fallback when a field is empty.
 */

const COUNTRIES = [
  { code: "US", label: "Estados Unidos" },
];

export default function AdminUpsSettings() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showSandboxSecret, setShowSandboxSecret] = useState(false);
  const [showProdSecret, setShowProdSecret] = useState(false);

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    api.get("/admin/settings/ups").then(({ data }) => setSettings(data)).catch(() => toast.error("No se pudieron cargar los ajustes"));
  }, []);

  if (!settings) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="font-mono-caps text-muted-foreground text-xs">Cargando…</div>
      </div>
    );
  }

  const set = (k) => (e) => setSettings({ ...settings, [k]: e.target.value });
  const setNum = (k) => (e) => setSettings({ ...settings, [k]: parseFloat(e.target.value) || 0 });
  const setBool = (k) => (v) => setSettings({ ...settings, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      // Only send explicit fields (never send masked bullets back)
      const payload = { ...settings };
      // Strip masked secrets: they start with • when unchanged
      if ((payload.sandbox_client_secret || "").includes("•")) delete payload.sandbox_client_secret;
      if ((payload.production_client_secret || "").includes("•")) delete payload.production_client_secret;
      delete payload.sandbox_secret_set;
      delete payload.production_secret_set;
      const { data } = await api.put("/admin/settings/ups", payload);
      setSettings(data);
      toast.success("Credenciales guardadas");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      // Save first (so unsaved edits are picked up)
      await save();
      const { data } = await api.post("/admin/settings/ups/test");
      toast.success(`Conexión OK · entorno ${data.env}`);
    } catch (e) {
      const err = e.response?.data?.detail;
      toast.error(err?.message || err || "OAuth de UPS falló");
    } finally {
      setTesting(false);
    }
  };

  const secretPlaceholder = (isSet) => (isSet ? "Guardado — deja vacío para no cambiarlo" : "Pega el secret aquí");

  return (
    <div className="min-h-[80vh] bg-cream" data-testid="admin-ups-settings">
      <section className="max-w-4xl mx-auto px-6 lg:px-12 py-16">
        <div className="mb-10">
          <div className="font-mono-caps text-xs text-muted-foreground">
            <Link to="/admin" className="hover:text-navy">Admin</Link> · Ajustes UPS
          </div>
          <h1 className="font-serif text-4xl md:text-5xl mt-3 flex items-center gap-3">
            <Truck strokeWidth={1.25} className="h-9 w-9" /> Credenciales UPS
          </h1>
          <p className="text-muted-foreground mt-4 max-w-2xl leading-relaxed">
            Configura tu integración UPS para calcular tarifas en el checkout y generar etiquetas reales desde el panel de pedidos.
            Crea una aplicación en el{" "}
            <a href="https://developer.ups.com/" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1 hover:text-navy">
              UPS Developer Portal <ExternalLink className="h-3 w-3" strokeWidth={1.25} />
            </a>{" "}
            y habilita las APIs <em>Rating</em> y <em>Shipping</em>.
          </p>
        </div>

        {/* Status banner */}
        <div className={`p-5 mb-8 border-2 ${settings.enabled ? "border-navy bg-navy/5" : "border-royal/40 bg-royal/5"}`} data-testid="ups-status-banner">
          <div className="flex items-start gap-3">
            {settings.enabled ? (
              <CheckCircle2 strokeWidth={1.25} className="h-5 w-5 text-navy mt-0.5" />
            ) : (
              <AlertTriangle strokeWidth={1.25} className="h-5 w-5 text-royal mt-0.5" />
            )}
            <div className="flex-1">
              <div className="font-mono-caps text-xs">
                {settings.enabled ? "UPS activo" : "UPS desactivado"} · Entorno: {settings.env}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {settings.enabled
                  ? "Los clientes verán tarifas UPS en el checkout y podrás generar etiquetas oficiales."
                  : "Rellena las credenciales, prueba la conexión y activa el interruptor para empezar."}
              </p>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <span className="font-mono-caps text-xs">Activar</span>
              <input
                type="checkbox"
                checked={!!settings.enabled}
                onChange={(e) => setBool("enabled")(e.target.checked)}
                className="h-5 w-5 accent-navy"
                data-testid="ups-enabled-toggle"
              />
            </label>
          </div>
        </div>

        <div className="bg-white border border-border p-8 space-y-10">
          {/* Environment */}
          <section>
            <h2 className="font-serif text-2xl mb-1">Entorno</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Empezamos siempre en <strong>sandbox</strong>. Cambia a producción sólo tras validar etiquetas de prueba.
            </p>
            <div className="grid grid-cols-2 gap-3 max-w-md">
              {["sandbox", "production"].map((e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => setSettings({ ...settings, env: e })}
                  className={`p-4 border transition-colors ${settings.env === e ? "border-navy bg-navy text-cream" : "border-border hover:border-navy"}`}
                  data-testid={`ups-env-${e}`}
                >
                  <div className="font-mono-caps text-xs">{e === "sandbox" ? "Sandbox" : "Producción"}</div>
                  <div className={`text-xs mt-1 ${settings.env === e ? "text-cream/70" : "text-muted-foreground"}`}>
                    {e === "sandbox" ? "wwwcie.ups.com" : "onlinetools.ups.com"}
                  </div>
                </button>
              ))}
            </div>
            <Field label="API Version" hint="Consulta la versión actual en el UPS Developer Portal (por defecto v2409).">
              <input value={settings.api_version} onChange={set("api_version")} className={inputCls} data-testid="ups-api-version" />
            </Field>
          </section>

          {/* Sandbox creds */}
          <section className="border-t border-border pt-8">
            <h2 className="font-serif text-2xl mb-1">Credenciales Sandbox</h2>
            <p className="text-sm text-muted-foreground mb-4">Del proyecto UPS marcado como "Test".</p>
            <Field label="Client ID">
              <input value={settings.sandbox_client_id} onChange={set("sandbox_client_id")} className={inputCls} data-testid="ups-sandbox-client-id" placeholder="p.ej. AbCdEfGhIjKl..." />
            </Field>
            <Field label="Client Secret" hint={settings.sandbox_secret_set ? "Ya guardado — deja vacío para conservarlo." : "Nunca se muestra tras guardar."}>
              <div className="relative">
                <input
                  type={showSandboxSecret ? "text" : "password"}
                  value={showSandboxSecret ? settings.sandbox_client_secret : (settings.sandbox_client_secret || "")}
                  onChange={set("sandbox_client_secret")}
                  className={inputCls}
                  placeholder={secretPlaceholder(settings.sandbox_secret_set)}
                  data-testid="ups-sandbox-client-secret"
                />
                <button type="button" onClick={() => setShowSandboxSecret((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 font-mono-caps text-[10px] text-muted-foreground hover:text-navy">
                  {showSandboxSecret ? "OCULTAR" : "MOSTRAR"}
                </button>
              </div>
            </Field>
          </section>

          {/* Production creds */}
          <section className="border-t border-border pt-8">
            <h2 className="font-serif text-2xl mb-1">Credenciales Producción</h2>
            <p className="text-sm text-muted-foreground mb-4">Sólo úsalas cuando la sandbox esté validada.</p>
            <Field label="Client ID">
              <input value={settings.production_client_id} onChange={set("production_client_id")} className={inputCls} data-testid="ups-prod-client-id" />
            </Field>
            <Field label="Client Secret" hint={settings.production_secret_set ? "Ya guardado — deja vacío para conservarlo." : ""}>
              <div className="relative">
                <input
                  type={showProdSecret ? "text" : "password"}
                  value={showProdSecret ? settings.production_client_secret : (settings.production_client_secret || "")}
                  onChange={set("production_client_secret")}
                  className={inputCls}
                  placeholder={secretPlaceholder(settings.production_secret_set)}
                  data-testid="ups-prod-client-secret"
                />
                <button type="button" onClick={() => setShowProdSecret((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 font-mono-caps text-[10px] text-muted-foreground hover:text-navy">
                  {showProdSecret ? "OCULTAR" : "MOSTRAR"}
                </button>
              </div>
            </Field>
          </section>

          {/* Account */}
          <section className="border-t border-border pt-8">
            <h2 className="font-serif text-2xl mb-1">Cuenta de facturación</h2>
            <p className="text-sm text-muted-foreground mb-4">Número de cuenta UPS (Shipper Number) usado para cargar los envíos.</p>
            <Field label="Nº de cuenta UPS (Shipper Number)">
              <input value={settings.account_number} onChange={set("account_number")} className={inputCls} data-testid="ups-account-number" placeholder="Ej. A1B2C3" />
            </Field>
          </section>

          {/* Origin */}
          <section className="border-t border-border pt-8">
            <h2 className="font-serif text-2xl mb-1">Dirección de origen (Ship From)</h2>
            <p className="text-sm text-muted-foreground mb-4">Dirección desde la que UPS recogerá los paquetes.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Empresa / Nombre">
                <input value={settings.origin_name} onChange={set("origin_name")} className={inputCls} data-testid="ups-origin-name" />
              </Field>
              <Field label="Teléfono">
                <input value={settings.origin_phone} onChange={set("origin_phone")} className={inputCls} data-testid="ups-origin-phone" />
              </Field>
              <Field label="Dirección" className="md:col-span-2">
                <input value={settings.origin_address1} onChange={set("origin_address1")} className={inputCls} data-testid="ups-origin-address" />
              </Field>
              <Field label="Ciudad">
                <input value={settings.origin_city} onChange={set("origin_city")} className={inputCls} data-testid="ups-origin-city" />
              </Field>
              <Field label="Provincia / Estado (código)">
                <input value={settings.origin_state} onChange={set("origin_state")} className={inputCls} data-testid="ups-origin-state" placeholder="M (Madrid)" />
              </Field>
              <Field label="Código Postal">
                <input value={settings.origin_postal} onChange={set("origin_postal")} className={inputCls} data-testid="ups-origin-postal" />
              </Field>
              <Field label="País">
                <select value={settings.origin_country} onChange={set("origin_country")} className={inputCls} data-testid="ups-origin-country">
                  {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                </select>
              </Field>
            </div>
          </section>

          {/* Package defaults */}
          <section className="border-t border-border pt-8">
            <h2 className="font-serif text-2xl mb-1">Paquete por defecto</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Usado cuando el pedido no especifica dimensiones. Unidades: <strong>libras (lb) y pulgadas (in)</strong> — así lo requiere UPS.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Field label="Peso por unidad (lb)">
                <input type="number" step="0.1" value={settings.default_weight_lb_per_item} onChange={setNum("default_weight_lb_per_item")} className={inputCls} data-testid="ups-default-weight" />
              </Field>
              <Field label="Largo (in)">
                <input type="number" step="0.1" value={settings.default_length_in} onChange={setNum("default_length_in")} className={inputCls} data-testid="ups-default-length" />
              </Field>
              <Field label="Ancho (in)">
                <input type="number" step="0.1" value={settings.default_width_in} onChange={setNum("default_width_in")} className={inputCls} data-testid="ups-default-width" />
              </Field>
              <Field label="Alto (in)">
                <input type="number" step="0.1" value={settings.default_height_in} onChange={setNum("default_height_in")} className={inputCls} data-testid="ups-default-height" />
              </Field>
            </div>
          </section>

          {/* Actions */}
          <section className="border-t border-border pt-8 flex flex-wrap gap-3">
            <button
              onClick={save}
              disabled={saving || testing}
              className="inline-flex items-center gap-2 bg-navy text-cream px-6 py-4 font-mono-caps hover:bg-cream hover:text-navy border border-navy transition-colors disabled:opacity-50"
              data-testid="ups-save"
            >
              <Save strokeWidth={1.25} className="h-4 w-4" />
              {saving ? "Guardando…" : "Guardar credenciales"}
            </button>
            <button
              onClick={test}
              disabled={saving || testing}
              className="inline-flex items-center gap-2 border-2 border-royal text-royal px-6 py-4 font-mono-caps hover:bg-royal hover:text-white transition-colors disabled:opacity-50"
              data-testid="ups-test"
            >
              <Zap strokeWidth={1.25} className="h-4 w-4" />
              {testing ? "Probando…" : "Probar conexión OAuth"}
            </button>
          </section>
        </div>
      </section>
    </div>
  );
}

const inputCls = "w-full border border-border p-3 focus:border-navy outline-none bg-white pr-20";

function Field({ label, hint, children, className = "" }) {
  return (
    <label className={`block mt-4 ${className}`}>
      <span className="font-mono-caps text-xs text-muted-foreground">{label}</span>
      <div className="mt-2">{children}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </label>
  );
}
